/**
 * Fix objects for validation issues (contract with the UI, see src/families/README.md):
 * `issue.fix = { label, patch }`. The patch is flat (top-level design keys, else family
 * parameters through `setParam`), so the UI can apply it as one undoable edit.
 */
import type { Design } from '../domain/schemas';
import type { Issue, IssueFix } from './assembly';
import { familyOf } from './registry';
import { setGridBaseKey } from './grid-bookcase/design';

/** Apply a fix's patch the way the UI does (src/app/fixes.ts `applyPatch`). */
export function applyFix(design: Design, fix: IssueFix): Design {
  const fam = familyOf(design);
  let next: Design = { ...design, appearance: { ...design.appearance } };
  for (const [k, v] of Object.entries(fix.patch)) {
    const val = JSON.parse(JSON.stringify(v)) as unknown;
    // a grid's base keys (top level) go through the grid so its openings refit to the new clear height
    const grid = next.params ? null : setGridBaseKey(next, k, val);
    if (grid) { next = grid; continue; }
    if (k in next && k !== 'params') (next as unknown as Record<string, unknown>)[k] = val;
    else if (next.params && fam.setParam) next = fam.setParam(next, k, val);
    else if (next.params) next = { ...next, params: { ...next.params, [k]: val } };
    else (next as unknown as Record<string, unknown>)[k] = val;
  }
  return next;
}

/** The first error that carries a fix, or null. */
export const firstFix = (errors: readonly Issue[]): IssueFix | null => errors.find((e) => e.fix)?.fix ?? null;
